import { FormFieldConfigModel, FormFieldConfigProp } from '@pxl/models/common';
import { hasValue } from '@pxl/shared/util';

type ModelType = {};

export type ConfigMapping<TModel extends ModelType, TConfig extends FormFieldConfigModel> = {
    sourceKey: keyof TModel;
    configKey: keyof TConfig;
};

export interface IModelDataValidator<TModel extends ModelType> {
    validateModel(model: TModel): boolean;

    getModelValidationMap(model: TModel): { key: string; isValid: boolean }[] | null;
}

export interface IConfigureModelDataValidator<TModel extends ModelType, TConfig extends FormFieldConfigModel> {
    mapPropToConfig(sKey: keyof TModel, cKey: keyof TConfig): void;

    ignoreProp(mKey: keyof TModel): void;
}

export class ModelDataValidator<TModel extends ModelType, TConfig extends FormFieldConfigModel>
    implements IModelDataValidator<TModel>, IConfigureModelDataValidator<TModel, TConfig>
{
    private readonly configKeys: string[] = [];

    public constructor(
        private readonly config: TConfig,
        private readonly mappings: ConfigMapping<TModel, TConfig>[] = [],
        private readonly ignoreKeys: (keyof TModel)[] = [],
    ) {
        if (!mappings) {
            this.mappings = [];
        }
        this.configKeys = Object.keys(this.config);
    }

    public validateModel(model: TModel): boolean {
        if (!model) {
            return false;
        }

        const keys = this.getModelKeys(model);

        for (const key of keys) {
            const modelValue = Object.getOwnPropertyDescriptor(model, key)?.value;

            if (!this.validateProp(modelValue, this.config[key], model)) {
                return false;
            }
        }

        for (const map of this.mappings) {
            const modelValue = Object.getOwnPropertyDescriptor(model, map.sourceKey)?.value;

            if (!this.validateProp(modelValue, this.config[map.configKey], model)) {
                return false;
            }
        }

        return true;
    }

    public getModelValidationMap(model: TModel): { key: string; isValid: boolean }[] | null {
        // This should be extended to include error type. Currently only the required error type.

        if (!model) {
            return null;
        }

        const keys = this.getModelKeys(model);
        const errorMap: { key: string; isValid: boolean }[] = [];

        for (const key of keys) {
            const modelValue = Object.getOwnPropertyDescriptor(model, key)?.value;

            errorMap.push({
                key,
                isValid: this.validateProp(modelValue, this.config[key], model),
            });
        }

        for (const map of this.mappings) {
            errorMap.push({
                key: map.sourceKey as string,
                isValid: this.validateProp(model[map.sourceKey], this.config[map.configKey], model),
            });
        }

        return errorMap;
    }

    public mapPropToConfig(sKey: keyof TModel, cKey: keyof TConfig): void {
        if (this.mappings.some((m) => m.sourceKey === sKey || m.configKey === cKey)) {
            return;
        }

        this.mappings.push({ sourceKey: sKey, configKey: cKey });
    }

    public ignoreProp(mKey: keyof TModel): void {
        if (this.ignoreKeys.includes(mKey)) {
            return;
        }

        this.ignoreKeys.push(mKey);
    }

    private getModelKeys(model: TModel): string[] {
        return Object.keys(model).filter(
            (sKey) => (!this.ignoreKeys || this.ignoreKeys.every((iKey) => sKey !== iKey)) && this.configKeys.includes(sKey),
        );
    }

    private validateProp(val: unknown, propConfig: FormFieldConfigProp, model?: TModel): boolean {
        // Here can a more complex implementation be added. covering different validators (ex. min or maxVal). At the moment it is only the (required) and (requiredOr) validators.
        const isRequiredValidator = () => {
            return !propConfig.isRequired || hasValue(val);
        };

        const isRequiredOrValidator = () => {
            if (!hasValue(propConfig.requiredOr) || hasValue(val) || !model) {
                return true;
            }

            return propConfig.requiredOr.some((requiredProp) => {
                return hasValue(model[<keyof TModel>requiredProp]);
            });
        };

        return [isRequiredValidator, isRequiredOrValidator].every((validator) => validator());
    }
}
